// Configuración por Producto — la tabla editable de Configuración.
//
// Son 724 productos y los tres campos que importan (valor unitario, tiempo de
// producción y MOQ) están todos en su valor por defecto: nadie los cargó nunca.
// De ahí las decisiones de esta pantalla:
//   · se PAGINA (50 por página) porque pintar 724 filas con inputs adentro traba;
//   · se BUSCA, porque encontrar un SKU entre 724 scrolleando no es viable;
//   · y sobre todo se EDITA POR LOTE, que es lo único que hace realista cargar
//     un catálogo entero — de a uno son 2.172 ediciones.
// El Excel del ticket #3 hace la carga masiva; esto es para corregir a mano.
import { useState } from 'react';
import { Check, Loader2, Search, X } from 'lucide-react';
import { Button, CeldaAuto, DataState, EditableCell, Table, Thead, Tr, Td } from '@/components/ui';
import {
  useAssignSupplier,
  useProducts,
  useSuppliers,
  useUpdateProduct,
  useUpdateProductsBulk,
} from '@/lib/hooks';
import { formatCop, formatNumber } from '@shared/format';
import { cn } from '@/lib/cn';
import type { Product, Proveedor } from '@shared/types';
import type { ProductConfigPatch } from '@/lib/api';

const POR_PAGINA = 50;

const COLUMNAS = [
  { key: 'sel', label: '' },
  { key: 'sku', label: 'SKU' },
  { key: 'producto', label: 'Producto' },
  { key: 'costo', label: 'Valor unitario', align: 'right' as const },
  { key: 'lead', label: 'Tiempo de producción', align: 'right' as const },
  { key: 'moq', label: 'MOQ', align: 'right' as const },
  // Los dos overrides de la 011. Llevan ayuda porque son los únicos campos de esta pantalla
  // que pueden estar en "automático", y qué significa eso no se adivina.
  { key: 'colchon', label: '% de colchón', align: 'right' as const, wrap: true, ayuda: 'colchonPct' as const },
  { key: 'reorden', label: 'Punto de reorden', align: 'right' as const, wrap: true, ayuda: 'puntoReorden' as const },
];

export function ProductConfigTable() {
  const products = useProducts();
  const editar = useUpdateProduct();
  const editarLote = useUpdateProductsBulk();

  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(0);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const proveedores = useSuppliers();
  const asignar = useAssignSupplier();
  const [aviso, setAviso] = useState<{ texto: string; tono: 'ok' | 'error' } | null>(null);

  return (
    <DataState query={products}>
      {(data) => {
        const filtrados = filtrar(data.items, busqueda);
        const paginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
        const pagActual = Math.min(pagina, paginas - 1);
        const visibles = filtrados.slice(pagActual * POR_PAGINA, (pagActual + 1) * POR_PAGINA);
        // Seleccionar/deseleccionar aplica a TODO lo filtrado, no solo a la página
        // visible: si buscás "cepillo" y son 120, querés los 120.
        const skusFiltrados = filtrados.map((p) => p.sku);
        const todosMarcados = skusFiltrados.length > 0 && skusFiltrados.every((s) => seleccion.has(s));

        const guardar = async (sku: string, patch: ProductConfigPatch) => {
          try {
            await editar.mutateAsync({ sku, patch });
            setAviso({ texto: `${sku} guardado`, tono: 'ok' });
          } catch (e) {
            setAviso({ texto: (e as Error).message, tono: 'error' });
          }
        };

        /** Asignar un proveedor al lote: no es un campo más del producto, es un vínculo — y
         *  además le materializa el tiempo de espera del proveedor a cada uno. */
        const asignarProveedor = async (id: number) => {
          const skus = [...seleccion];
          try {
            const res = await asignar.mutateAsync({ id, skus });
            setAviso({
              texto:
                id === 0
                  ? `${res.asignados} producto${res.asignados !== 1 ? 's' : ''} sin proveedor`
                  : `${res.asignados} producto${res.asignados !== 1 ? 's' : ''} · ${res.leadTimeDias} días de espera`,
              tono: 'ok',
            });
            setSeleccion(new Set());
          } catch (e) {
            setAviso({ texto: (e as Error).message, tono: 'error' });
          }
        };

        const aplicarLote = async (patch: ProductConfigPatch) => {
          const skus = [...seleccion];
          try {
            const res = await editarLote.mutateAsync({ skus, patch });
            setAviso({ texto: `${res.updated} producto${res.updated !== 1 ? 's' : ''} actualizado${res.updated !== 1 ? 's' : ''}`, tono: 'ok' });
            setSeleccion(new Set());
          } catch (e) {
            setAviso({ texto: (e as Error).message, tono: 'error' });
          }
        };

        return (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[240px] flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
                <input
                  value={busqueda}
                  onChange={(e) => { setBusqueda(e.target.value); setPagina(0); }}
                  placeholder="Buscar por SKU, producto, categoría o proveedor…"
                  className="h-10 w-full rounded-xl border border-hairline bg-panel pl-9 pr-9 text-sm text-ink-strong placeholder:text-ink-faint focus:border-primary focus:outline-none"
                />
                {busqueda && (
                  <button
                    onClick={() => { setBusqueda(''); setPagina(0); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-strong"
                    aria-label="Limpiar búsqueda"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-2xs text-ink-faint">
                <input
                  type="checkbox"
                  checked={todosMarcados}
                  onChange={(e) => setSeleccion(e.target.checked ? new Set(skusFiltrados) : new Set())}
                  className="h-4 w-4 cursor-pointer accent-primary"
                />
                Marcar todo{busqueda && ' lo filtrado'}
              </label>
              <p className="text-2xs text-ink-faint">
                {filtrados.length === data.items.length
                  ? `${formatNumber(data.items.length)} productos`
                  : `${formatNumber(filtrados.length)} de ${formatNumber(data.items.length)}`}
              </p>
            </div>

            {seleccion.size > 0 && (
              <BarraLote
                proveedores={proveedores.data?.items ?? []}
                onAsignarProveedor={asignarProveedor}
                cantidad={seleccion.size}
                guardando={editarLote.isPending}
                onAplicar={aplicarLote}
                onLimpiar={() => setSeleccion(new Set())}
              />
            )}

            {aviso && (
              <p className={cn('text-2xs', aviso.tono === 'ok' ? 'text-success-text' : 'text-danger-text')}>
                {aviso.texto}
              </p>
            )}

            {filtrados.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-faint">
                Ningún producto coincide con «{busqueda}».
              </p>
            ) : (
              <Table>
                <Thead columns={COLUMNAS} />
                <tbody>
                  {visibles.map((p) => (
                    <FilaProducto
                      key={p.sku}
                      producto={p}
                      marcado={seleccion.has(p.sku)}
                      onMarcar={(marcado) =>
                        setSeleccion((prev) => {
                          const next = new Set(prev);
                          if (marcado) next.add(p.sku);
                          else next.delete(p.sku);
                          return next;
                        })
                      }
                      onGuardar={(patch) => guardar(p.sku, patch)}
                    />
                  ))}
                </tbody>
              </Table>
            )}

            {paginas > 1 && (
              <div className="flex items-center justify-between gap-3 pt-1">
                <p className="text-2xs text-ink-faint">
                  Mostrando {formatNumber(pagActual * POR_PAGINA + 1)}–
                  {formatNumber(Math.min((pagActual + 1) * POR_PAGINA, filtrados.length))} de{' '}
                  {formatNumber(filtrados.length)}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" disabled={pagActual === 0} onClick={() => setPagina(pagActual - 1)}>
                    Anterior
                  </Button>
                  <span className="text-2xs text-ink-faint">
                    {pagActual + 1} / {paginas}
                  </span>
                  <Button variant="outline" disabled={pagActual >= paginas - 1} onClick={() => setPagina(pagActual + 1)}>
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      }}
    </DataState>
  );
}

/** Busca en SKU, nombre, categoría y proveedor. Sin tildes ni mayúsculas. */
function filtrar(items: Product[], busqueda: string): Product[] {
  const q = normalizar(busqueda);
  if (!q) return items;
  return items.filter((p) =>
    normalizar(`${p.sku} ${p.name} ${p.category} ${p.supplier}`).includes(q),
  );
}

// NFD separa la tilde de la letra y el rango borra los acentos: así "cepillo"
// encuentra "CEPÍLLO" y viceversa (el catálogo del cliente los usa mezclados).
const normalizar = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

// ── Barra de edición por lote ────────────────────────────────────────────────
// Solo aplica los campos que se completaron: dejar uno vacío es "no lo toques".
// Así se puede poner el lead time de 200 productos sin pisarles el MOQ.
function BarraLote({
  cantidad,
  guardando,
  onAplicar,
  onLimpiar,
  proveedores,
  onAsignarProveedor,
}: {
  cantidad: number;
  guardando: boolean;
  onAplicar: (patch: ProductConfigPatch) => void;
  onLimpiar: () => void;
  proveedores: Proveedor[];
  onAsignarProveedor: (id: number) => void;
}) {
  const [costo, setCosto] = useState('');
  const [lead, setLead] = useState('');
  const [moq, setMoq] = useState('');
  const [colchon, setColchon] = useState('');

  const patch: ProductConfigPatch = {};
  if (costo.trim() !== '') patch.unitCost = Number(costo);
  if (lead.trim() !== '') patch.leadTimeDays = Number(lead);
  if (moq.trim() !== '') patch.moq = Number(moq);
  // ⚠️ El % de colchón SÍ va por lote —"todos los importados con 15 %" es una decisión de
  // política y se toma para un grupo— pero el PUNTO DE REORDEN no: son unidades, y el mismo
  // número para 200 productos con demandas distintas no significa nada. Poner 500 a todos
  // sería la forma más rápida de romper el catálogo entero, y con un solo clic.
  if (colchon.trim() !== '') patch.safetyPct = Number(colchon);
  const hayAlgo = Object.keys(patch).length > 0;

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-primary/30 bg-primary/[0.06] p-3">
      <p className="text-xs font-medium text-ink-strong">
        {formatNumber(cantidad)} seleccionado{cantidad !== 1 ? 's' : ''}
      </p>
      <CampoLote label="Valor unitario" value={costo} onChange={setCosto} placeholder="$" />
      {/* El proveedor va aparte de los campos de arriba y NO es un "campo vacío = no tocar":
          elegirlo aplica en el acto, porque además le escribe a cada producto el tiempo de
          espera del proveedor. Mezclarlo con el resto haría que un "Aplicar" con el
          desplegable en cualquier posición cambiara el vínculo sin querer. */}
      {proveedores.length > 0 && (
        <label className="flex flex-col gap-1">
          <span className="text-2xs uppercase tracking-wide text-ink-faint">Proveedor</span>
          <select
            defaultValue=""
            disabled={guardando}
            onChange={(e) => {
              if (e.target.value === '') return;
              onAsignarProveedor(Number(e.target.value));
              e.target.value = '';
            }}
            className="h-8 rounded-lg border border-hairline bg-card px-2 text-xs text-ink focus:border-primary focus:outline-none"
          >
            <option value="">Asignar a…</option>
            {proveedores.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre} · {p.leadTimeDias} d
              </option>
            ))}
            <option value="0">— sin proveedor —</option>
          </select>
        </label>
      )}
      <CampoLote label="Tiempo de producción" value={lead} onChange={setLead} placeholder="días" />
      <CampoLote label="MOQ" value={moq} onChange={setMoq} placeholder="uds" />
      <CampoLote label="% de colchón" value={colchon} onChange={setColchon} placeholder="%" />
      <Button
        variant="primary"
        disabled={!hayAlgo || guardando}
        onClick={() => onAplicar(patch)}
        title={hayAlgo ? undefined : 'Completá al menos un campo'}
      >
        {guardando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        Aplicar a {formatNumber(cantidad)}
      </Button>
      <Button variant="ghost" onClick={onLimpiar} disabled={guardando}>
        Cancelar
      </Button>
      <p className="w-full text-2xs text-ink-faint">
        Los campos que dejes vacíos no se tocan. El punto de reorden se fija de a uno: son
        unidades, y el mismo número para productos con demandas distintas no querría decir nada.
      </p>
    </div>
  );
}

function CampoLote({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-2xs text-ink-faint">{label}</span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 w-32 rounded-lg border border-hairline bg-panel px-2 text-right text-sm text-ink-strong focus:border-primary focus:outline-none"
      />
    </label>
  );
}

// ── Fila ─────────────────────────────────────────────────────────────────────
function FilaProducto({
  producto,
  marcado,
  onMarcar,
  onGuardar,
}: {
  producto: Product;
  marcado: boolean;
  onMarcar: (marcado: boolean) => void;
  onGuardar: (patch: ProductConfigPatch) => void;
}) {
  const ropCalculado = producto.puntoReordenCalculado;
  // Mismo umbral que la ficha del producto, y por el mismo motivo: un aviso que salta por
  // cualquier diferencia deja de leerse. Ver DESVIO_AVISO en FichaProducto.tsx.
  const desviado =
    producto.reorderPoint != null &&
    ropCalculado != null &&
    ropCalculado > 0 &&
    Math.abs(producto.reorderPoint - ropCalculado) / ropCalculado > 0.3;
  return (
    <Tr className={marcado ? 'bg-primary/[0.06]' : undefined}>
      <Td>
        <input
          type="checkbox"
          checked={marcado}
          onChange={(e) => onMarcar(e.target.checked)}
          className="h-4 w-4 cursor-pointer accent-primary"
          aria-label={`Seleccionar ${producto.sku}`}
        />
      </Td>
      <Td>
        <span className="font-mono text-2xs text-ink-faint">{producto.sku}</span>
      </Td>
      <Td>
        <p className="font-medium text-ink-strong">{producto.name}</p>
        <p className="text-2xs text-ink-faint">
          {producto.category} · {producto.supplier}
        </p>
      </Td>
      <Td align="right">
        <EditableCell
          valor={producto.unitCost}
          formato={formatCop}
          onGuardar={(v) => onGuardar({ unitCost: v })}
          sinCargar={producto.unitCost === 0}
        />
      </Td>
      <Td align="right">
        <EditableCell
          valor={producto.leadTimeDays}
          formato={(v) => `${formatNumber(v)}d`}
          onGuardar={(v) => onGuardar({ leadTimeDays: v })}
        />
      </Td>
      <Td align="right">
        <EditableCell
          valor={producto.moq}
          formato={(v) => `${formatNumber(v)}`}
          sufijo="uds"
          onGuardar={(v) => onGuardar({ moq: v })}
        />
      </Td>
      <Td align="right">
        <CeldaAuto
          valor={producto.safetyPct}
          auto={producto.colchonPctCalculado}
          formato={(v) => `${v}%`}
          onGuardar={(v) => onGuardar({ safetyPct: v })}
        />
      </Td>
      <Td align="right">
        <CeldaAuto
          valor={producto.reorderPoint}
          auto={ropCalculado}
          formato={(v) => formatNumber(v)}
          onGuardar={(v) => onGuardar({ reorderPoint: v })}
        />
        {/* El aviso vive también acá, no sólo en la ficha: ésta es la pantalla donde se
            arregla, y mandarlo a buscar a otro lado sería fricción sin motivo. */}
        {desviado && (
          <span className="mt-0.5 block text-2xs leading-tight text-warning-text">
            hoy daría {formatNumber(ropCalculado!)}
          </span>
        )}
      </Td>
    </Tr>
  );
}
